import React from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faSearch,
  faEdit,
  faTimes,
  faTimesCircle,
  faInfoCircle,
} from "@fortawesome/free-solid-svg-icons";
import { useLoading } from "../../../../../contexts/LoadingContext";
import { useTranslation } from "../../../../../i18n/LanguageContext";
import SocialMediaAnalysisAPI from "../../../services/SocialMediaAnalysisAPI";
import InternationalPhoneInput from "../../../utils/InternationalPhoneInputUtil.js";
import "../css/SocialMediaAnalysisDesktop.css";

const AnalysisInput = ({
  formData,
  setFormData,
  formErrors,
  setFormErrors,
  error,
  onSubmit,
  onReset,
  isMobile = false,
  isOptimized = false,
  hideHeader = false,
}) => {
  const { isLoading } = useLoading();
  const { t } = useTranslation();

  // Check if any field has a value (not empty or default)
  const hasFormValues = () => {
    return (
      formData.businessName.trim() !== "" ||
      formData.websiteUrl.trim() !== "" ||
      formData.merchandiseType !== "" ||
      formData.businessInteractionReason !== "" ||
      formData.businessContactName.trim() !== "" ||
      formData.contactPhone !== "" ||
      formData.socialMediaPlatform !== "" ||
      (formData.userContext && formData.userContext.trim() !== "")
    );
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    // Clear error for this field when user starts typing
    if (formErrors[name]) {
      setFormErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }

    // Phone number validation for E.164 format
    // Note: InternationalPhoneInput component handles E.164 formatting automatically

    // If user is typing in businessName or websiteUrl and there's content,
    // clear the "at least one required" error from both fields
    if ((name === "businessName" || name === "websiteUrl") && value.trim()) {
      setFormErrors((prev) => {
        const newErrors = { ...prev };
        // Only clear if the error message is the "at least one required" message
        if (
          newErrors.businessName &&
          newErrors.businessName.includes("Please enter either")
        ) {
          delete newErrors.businessName;
        }
        if (
          newErrors.websiteUrl &&
          newErrors.websiteUrl.includes("Please enter either")
        ) {
          delete newErrors.websiteUrl;
        }
        return newErrors;
      });
    }
  };

  const handlePhoneChange = (phoneValue) => {
    setFormData((prev) => ({
      ...prev,
      contactPhone: phoneValue,
    }));

    // Clear phone error when user changes phone
    if (formErrors.contactPhone) {
      setFormErrors((prev) => ({
        ...prev,
        contactPhone: "",
      }));
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit(e);
  };

  return (
    <div className={hideHeader ? "left-panel-tab-content" : "left-panel"}>
      {!isMobile && !hideHeader && (
        <div className="common-section-header">
          <h3>
            <FontAwesomeIcon icon={faEdit} />
            {t("social.tab.inputs")}
          </h3>
        </div>
      )}
      <div className="panel-content inputs-panel-content">
        {error && (
          <div className="alert alert-error">
            <FontAwesomeIcon icon={faTimesCircle} />
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="case-form">
          <label className="business-info">
            {t("social.form.label.businessInfo")}
          </label>
          <div className="form-section business-container">
            <div className="form-group">
              <label htmlFor="businessName">
                {t("social.form.label.businessName")}
              </label>
              <input
                type="text"
                id="businessName"
                name="businessName"
                value={formData.businessName}
                onChange={handleInputChange}
                className={`form-control ${
                  formErrors.businessName ? "error" : ""
                }`}
                placeholder={t("social.form.placeholder.businessName")}
                disabled={isLoading}
              />
              {formErrors.businessName && (
                <span className="error-message">{formErrors.businessName}</span>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="websiteUrl">
                {t("social.form.label.websiteUrl")}
              </label>
              <input
                type="text"
                id="websiteUrl"
                name="websiteUrl"
                value={formData.websiteUrl}
                onChange={handleInputChange}
                className={`form-control ${
                  formErrors.websiteUrl ? "error" : ""
                }`}
                placeholder={t("social.form.placeholder.websiteUrl")}
                disabled={isLoading}
              />
              {formErrors.websiteUrl && (
                <span className="error-message">{formErrors.websiteUrl}</span>
              )}
            </div>
          </div>
          <div className="form-group">
            <label htmlFor="socialMediaPlatform">
              {t("social.form.label.platform")}
            </label>
            <select
              id="socialMediaPlatform"
              name="socialMediaPlatform"
              value={formData.socialMediaPlatform}
              onChange={handleInputChange}
              className="form-control"
              disabled={isLoading}
            >
              <option value="">
                {t("social.form.placeholder.selectPlatform")}
              </option>
              {SocialMediaAnalysisAPI.getSocialMediaPlatforms().map(
                (platform) => (
                  <option key={platform} value={platform}>
                    {platform}
                  </option>
                ),
              )}
            </select>
          </div>

          <div className="form-group">
            <label htmlFor="merchandiseType">
              {t("social.form.label.merchandise")}
            </label>
            <select
              id="merchandiseType"
              name="merchandiseType"
              value={formData.merchandiseType}
              onChange={handleInputChange}
              className="form-control"
              disabled={isLoading}
            >
              <option value="">
                {t("social.form.placeholder.selectType")}
              </option>
              {SocialMediaAnalysisAPI.getMerchandiseTypes().map((type) => (
                <option key={type} value={type}>
                  {type}
                </option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label htmlFor="businessInteractionReason">
              {t("social.form.label.interaction")}
            </label>
            <select
              id="businessInteractionReason"
              name="businessInteractionReason"
              value={formData.businessInteractionReason}
              onChange={handleInputChange}
              className="form-control"
              disabled={isLoading}
            >
              <option value="">
                {t("social.form.placeholder.selectMethod")}
              </option>
              {SocialMediaAnalysisAPI.getInteractionReasons().map((reason) => (
                <option key={reason} value={reason}>
                  {reason}
                </option>
              ))}
            </select>
          </div>

          <div className="form-row-half">
            <div className="form-group">
              <label htmlFor="businessContactName">
                {t("social.form.label.contactPerson")}
              </label>
              <input
                type="text"
                id="businessContactName"
                name="businessContactName"
                value={formData.businessContactName}
                onChange={handleInputChange}
                className="form-control"
                placeholder={t("social.form.placeholder.contactPerson")}
                disabled={isLoading}
              />
            </div>

            <div className="form-group">
              <label htmlFor="contactPhone">
                {t("social.form.label.contactPhone")}
              </label>
              <InternationalPhoneInput
                value={formData.contactPhone}
                onChange={handlePhoneChange}
                error={formErrors.contactPhone}
                disabled={isLoading}
                placeholder={t("social.form.placeholder.phone")}
              />
              <small className="field-help">
                {t("social.form.hint.phone")}
              </small>
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="userContext">
              <FontAwesomeIcon icon={faInfoCircle} className="label-icon" />
              {t("social.form.label.context")}
            </label>
            <textarea
              id="userContext"
              name="userContext"
              value={formData.userContext || ""}
              onChange={handleInputChange}
              className="form-control"
              placeholder={t("social.form.placeholder.context")}
              rows={3}
              disabled={isLoading}
            />
          </div>

          <div className="btn-group">
            <button
              type="submit"
              className="btn btn-primary"
              disabled={
                isLoading ||
                (!formData.businessName.trim() && !formData.websiteUrl.trim())
              }
            >
              {isLoading ? (
                <>
                  <FontAwesomeIcon icon={faSearch} /> &nbsp;{" "}
                  {t("social.form.button.analyzing")}
                </>
              ) : (
                <>
                  <FontAwesomeIcon icon={faSearch} /> &nbsp;{" "}
                  {t("social.form.button.analyze")}
                </>
              )}
            </button>

            <button
              type="button"
              onClick={onReset}
              className="btn btn-ghost"
              disabled={isLoading || !hasFormValues()}
            >
              <FontAwesomeIcon icon={faTimes} /> &nbsp;{" "}
              {t("social.form.button.clear")}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AnalysisInput;
